import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { finalize, switchMap, takeUntil, tap } from 'rxjs/operators';
import { CompanyModel } from '@data-layer/models/companies';
import { UntypedFormControl } from '@angular/forms';
import { CustomValidator } from '@helpers/custom-validator';
import { ALLOWED_FILE_EXTENSIONS, ALLOWED_FILE_MIMES, POA_FILE_URL } from '@constants/constants';
import { environment } from '@env/environment';
import { DocumentCreateModel, DocumentModel } from '@data-layer/models/documents';
import { Observable, ReplaySubject } from 'rxjs';
import { DocumentsService } from '@services/documents.service';
import {
  enumDocumentRelatedEntityType,
  enumDocumentRootEntityType,
  enumDocumentState,
  enumDocumentType,
} from '@data-layer/enums/documents';
import { EntrySummaryModel } from '@data-layer/models/entry-summaries';
import { EntrySummaryStateService } from '@shared/es-mutual/services';
import { enumEntrySummaryDutiesPaymentType, enumTransportationMode } from '@data-layer/enums/entry-summaries';
import { TrackByService } from '@app/shared/services';
import { UserBetaFlagModel } from '@data-layer/models/user';
import { CompaniesService } from '@services/companies.service';
import { enumUserBetaFlag } from '@data-layer/enums/user';

@Component({
  selector: 'desktop-upload-entry-summary-documents',
  template: `
    <desktop-entry-summary-card cardTitle="LABELS.ADD_DOCUMENTS" [isValid]="isValidDocs">
      <ng-container header>
        <div class="d-flex align-items-center">
          <div class="download-all-files" *ngIf="isUploadedAnyFiles && !isHideDownloadAll" (click)="onDownloadAllFiles()">
            {{ 'LABELS.DOWNLOAD_ALL_FILES' | translate }}
          </div>

          <div class="btn-group add-documents d-print-none" dropdown>
            <button id="button-files" dropdownToggle type="button" class="btn btn-link dropdown-toggle">
              <span class="wrapper-icon-add"><i class="fa fa-plus"></i></span>
              <span>{{ 'BUTTON.ADD_OTHER_DOCUMENT' | translate }}</span>
              <span class="wrapper-icon-carret"><i class="fa fa-caret-down"></i></span>
            </button>
            <ul id="dropdown-files" class="dropdown-menu" role="menu" *dropdownMenu>
              <li role="menuitem" *ngFor="let doc of availableDocTypes" (click)="onAddOtherDocument(doc)">
                <span class="dropdown-item">
                  {{ (isAdditionalDocPrefix(doc) ? 'LABELS.ADDITIONAL' : '') | translate }}
                  {{ 'DOCUMENT_TYPE.' + enumDocumentType[doc] | translate }}
                </span>
              </li>
            </ul>
          </div>
        </div>
      </ng-container>

      <ng-container body>
        <div class="document-upload-wrapper">
          <span class="document-upload-hint">{{ 'LABELS.ADD_DOCUMENTS_ES_HINT' | translate }}</span>

          <div class="document-upload-items d-flex flex-wrap">
            <div
              class="document-upload-item"
              [ngClass]="{ 'is-sidebar': isSidebar }"
              *ngFor="let document of attachedDocuments; let index = index; trackBy: trackByFn"
            >
              <document-upload-desktop
                [id]="'entry-summary-documents-' + entrySummary.id + '-' + index + (document.id ? '-' + document.id : '')"
                [attachmentName]="'DOCUMENT_TYPE.' + enumDocumentType[document.type]"
                [attachmentTypeName]="'LABELS.ENTRY_SUMMARY' | translate | uppercase"
                [attachmentHint]="getAttachmentHint(document)"
                [poaFileUrl]="POA_FILE_URL"
                [footerHint]="getAttachmentFooter(document)"
                [isRequired]="getAttachmentRequired(document)"
                [isStretched]="true"
                [uploadedDocument]="document"
                [canUploadDocuments]="isCanEdit || !document.uploadedDate"
                [canRemoveDocuments]="isCanEdit && !isHideDelete"
                (upload)="
                  [enumDocumentType.POWER_OF_ATTORNEY, enumDocumentType.POWER_OF_ATTORNEY_TRUCKER].includes(document.type)
                    ? isSkipAddPoaToCompanyPrompt
                      ? uploadPOADocument(document, $event, false, false)
                      : onUploadPoaFile(document, $event)
                    : uploadDocumentCall(document, $event)
                "
                (remove)="removeDocument(document.id)"
                (stateUpdate)="onDocumentStateUpdate(document.id, $event)"
              ></document-upload-desktop>
            </div>
          </div>
        </div>

        <loading-spinner [show]="isLoading"></loading-spinner>
      </ng-container>
    </desktop-entry-summary-card>

    <input class="d-none" type="file" #input [accept]="ALLOWED_FILE_MIMES.join(',')" (change)="handleFiles($event)" />

    <custom-modal [(open)]="isOpenPoaModal" [header]="'DOCUMENTS.UPLOAD_POA_TO_COMPANY_MODAL.HEADER'">
      {{ 'DOCUMENTS.UPLOAD_POA_TO_COMPANY_MODAL.BODY' | translate }}

      <button footer-action type="button" class="btn btn-primary" (click)="uploadPOADocument(temporaryDoc, temporaryFile, false, true)">
        {{ 'LABELS.YES' | translate }}
      </button>
      <button footer-action type="button" class="btn btn-secondary" (click)="uploadPOADocument(temporaryDoc, temporaryFile, false, false)">
        {{ 'LABELS.NO' | translate }}
      </button>
    </custom-modal>
  `,
  styleUrls: ['./es-upload-documents-desktop.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EsUploadDocumentsDesktopComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public company: CompanyModel;
  @Input() public entrySummary: EntrySummaryModel;
  @Input() public attachedDocuments: DocumentModel[] = [];
  @Input() public isCanEdit: boolean;
  @Input() public isSidebar: boolean;
  @Input() public hasPendingBills: boolean;
  @Input() public isRequiredDocument: boolean = true;
  @Input() public isHideDelete: boolean;
  @Input() public isHideDownloadAll: boolean;
  @Input() public isSkipAddPoaToCompanyPrompt: boolean = false;
  @Output() public downloadAllFiles: EventEmitter<number[]> = new EventEmitter<number[]>();

  @ViewChild('input', { static: true }) public inputRef: ElementRef;

  public enumDocumentType: typeof enumDocumentType = enumDocumentType;
  public ALLOWED_FILE_MIMES: typeof ALLOWED_FILE_MIMES = ALLOWED_FILE_MIMES;

  public allAttachedDocuments: DocumentModel[] = [];
  public availableDocTypes: enumDocumentType[];
  public isLoading: boolean;
  public fileControl: UntypedFormControl = new UntypedFormControl(null, [
    CustomValidator.fileExtensions(ALLOWED_FILE_EXTENSIONS),
    CustomValidator.maxFileSize(environment.files.default_file_size_limit),
  ]);

  public currentClickedType: enumDocumentType;

  public isOpenPoaModal: boolean;
  public temporaryDoc: DocumentCreateModel;
  public temporaryFile: File;
  public POA_FILE_URL: typeof POA_FILE_URL = POA_FILE_URL;

  public trackByFn: (item: any) => string | number | string = TrackByService.trackById;

  private isESSynced: boolean;

  private destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>();

  constructor(
    private changeDetector: ChangeDetectorRef,
    private documentsService: DocumentsService,
    private entrySummaryStateService: EntrySummaryStateService,
    private companiesService: CompaniesService,
  ) {}

  public ngOnInit(): void {
    this.availableDocTypes = this.initDocumentTypes();

    this.isESSynced = this.entrySummaryStateService.isESSynced(this.entrySummary.state);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes?.attachedDocuments) {
      if (!!this.entrySummary) {
        this.isESSynced = this.entrySummaryStateService.isESSynced(this.entrySummary.state);
      }

      this.allAttachedDocuments = [...this.attachedDocuments];
      this.filterFiles(this.attachedDocuments);
    }
    if (changes?.entrySummary) {
      this.availableDocTypes = this.initDocumentTypes();
      this.isESSynced = this.entrySummaryStateService.isESSynced(this.entrySummary.state);

      this.allAttachedDocuments = [...this.attachedDocuments];
      this.filterFiles(this.attachedDocuments, true);
    }
  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  public onAddOtherDocument(type: enumDocumentType): void {
    this.currentClickedType = type;
    this.inputRef.nativeElement.click();
  }

  public handleFiles(event: Event): void {
    const target: HTMLInputElement = event.target as HTMLInputElement;
    const files: FileList = target.files;
    if (files && files.length) {
      this.isLoading = true;
      this.uploadFile(files[0], (file: File) => {
        this.uploadDocument(
          {
            relatedEntityId: this.entrySummary.id,
            relatedEntityType: enumDocumentRelatedEntityType.ENTRY_SUMMARY,
            rootEntityType: enumDocumentRootEntityType.ENTRY_SUMMARY,
            rootEntityId: this.entrySummary.id,
            type: this.currentClickedType,
            fileName: file.name,
          },
          file,
        )
          .pipe(
            takeUntil(this.destroy$),
            finalize(() => (this.isLoading = false)),
          )
          .subscribe();
      });
    }
    this.inputRef.nativeElement.value = '';
  }

  public onDocumentStateUpdate(documentId: number, state: enumDocumentState): void {
    this.attachedDocuments = this.attachedDocuments.map((document: DocumentModel) =>
      document.id === documentId ? { ...document, state } : document,
    );
    this.changeDetector.markForCheck();
  }

  public uploadDocument(document: DocumentCreateModel, file: File): Observable<any> {
    return this.documentsService.uploadDocument(document, file, this.company.id).pipe(switchMap(() => this.loadDocuments(false)));
  }

  public uploadDocumentCall(document: DocumentCreateModel, file: File): void {
    this.uploadDocument(document, file).pipe(takeUntil(this.destroy$)).subscribe();
  }

  public uploadPOADocument(document: DocumentCreateModel, file: File, isTrucker: boolean, uploadToCompany?: boolean): void {
    this.isOpenPoaModal = false;
    this.temporaryDoc = null;
    this.temporaryFile = null;

    this.entrySummaryStateService
      .uploadPoaDocument({ ...document, fileName: file?.name }, file, uploadToCompany, this.hasPendingBills, isTrucker, this.company.id)
      .pipe(
        switchMap(() => this.loadDocuments(false)),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  public removeDocument(documentId: number): void {
    this.documentsService
      .removeDocument(documentId, true)
      .pipe(
        switchMap(() => this.loadDocuments(false)),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  public getAttachmentHint(document: DocumentModel): string {
    if (document.type === enumDocumentType.POWER_OF_ATTORNEY) {
      return 'LABELS.POWER_OF_ATTORNEY_HINT';
    } else if (document.type === enumDocumentType.CARGO_MANIFEST) {
      return 'CARGO_MANIFEST_HINT';
    } else if (document.type === enumDocumentType.ARRIVAL_NOTICE) {
      return 'LABELS.ARRIVAL_NOTICE_HINT';
    } else if (document.type === enumDocumentType.PACKING_LIST) {
      return 'LABELS.PACKING_LIST_HINT';
    }

    return null;
  }

  public getAttachmentRequired(document: DocumentModel): boolean {
    if (!this.isRequiredDocument) {
      return false;
    }

    switch (document.type) {
      case enumDocumentType.POWER_OF_ATTORNEY:
      case enumDocumentType.POWER_OF_ATTORNEY_TRUCKER:
      case enumDocumentType.FINAL_STATEMENT:
      case enumDocumentType.PRELIMINARY_STATEMENT:
        return true;
      case enumDocumentType.ACH_APPROVAL:
        return this.entrySummary.dutiesPaymentType === enumEntrySummaryDutiesPaymentType.ACH;
      default:
        return false;
    }
  }

  public getAttachmentFooter(document: DocumentModel): string {
    if ([enumDocumentType.POWER_OF_ATTORNEY, enumDocumentType.POWER_OF_ATTORNEY_TRUCKER].includes(document.type)) {
      return 'LABELS.DRAG_N_DROP_FOR_UPLOAD_POA';
    } else {
      return 'LABELS.DRAG_N_DROP_FOR_UPLOAD';
    }

    return null;
  }

  public isAdditionalDocPrefix(documentType: enumDocumentType): boolean {
    if ([enumDocumentType.PACKING_LIST, enumDocumentType.ARRIVAL_NOTICE].includes(documentType)) {
      return true;
    } else {
      return false;
    }
  }

  public onUploadPoaFile(document: DocumentCreateModel, file: File): void {
    this.onUploadPoaCompanyFile(document, file);
  }

  public onUploadPoaCompanyFile(document: DocumentCreateModel, file: File): void {
    this.isOpenPoaModal = true;
    this.temporaryDoc = document;
    this.temporaryFile = file;
  }

  public get isValidDocs(): boolean {
    return this.attachedDocuments
      .filter((doc: DocumentModel) => doc.type === enumDocumentType.POWER_OF_ATTORNEY)
      .every((doc: DocumentModel) => [enumDocumentState.SCAN_PENDING, enumDocumentState.UPLOADED].includes(doc.state));
  }

  public onDownloadAllFiles(): void {
    this.downloadAllFiles.emit(this.allAttachedDocuments.filter((item: DocumentModel) => !!item.id).map((item: DocumentModel) => item.id));
  }

  public get isUploadedAnyFiles(): boolean {
    return !!this.attachedDocuments.filter((item: DocumentModel) => !!item.id).length;
  }

  public get isHasEsOcrRedesign(): boolean {
    return this.companiesService
      .getUser()
      ?.betaFlags?.map((flag: UserBetaFlagModel) => flag.tag)
      .includes(enumUserBetaFlag.esOcrRedesign);
  }

  private initDocumentTypes(): enumDocumentType[] {
    const result: enumDocumentType[] = [
      enumDocumentType.ARRIVAL_NOTICE,
      enumDocumentType.PACKING_LIST,
      enumDocumentType.TRADE_CERTIFICATE,
      enumDocumentType.UNKNOWN,
    ];

    // #5820
    if (this.isESSynced) {
      result.unshift(enumDocumentType.INVOICE);
    }

    return result;
  }

  private uploadFile(file: File, callBack: (file: File) => void): void {
    this.clearFile();
    this.fileControl.patchValue(file);
    this.fileControl.markAsTouched();
    if (this.fileControl.valid) {
      callBack(file);
    }
    this.inputRef.nativeElement.value = null;
  }

  private clearFile(): void {
    this.fileControl.patchValue(null);
    this.fileControl.markAsUntouched();
  }

  private loadDocuments(isShowLoader: boolean): Observable<DocumentModel[]> {
    return this.entrySummaryStateService.updateDocuments(isShowLoader).pipe(
      takeUntil(this.destroy$),
      tap((documents: DocumentModel[]) => {
        this.filterFiles(documents);
        this.changeDetector.markForCheck();
      }),
    );
  }

  private filterFiles(documents: DocumentModel[], isReload?: boolean): void {
    if (isReload) {
      this.attachedDocuments = [];
    }

    const typesToSkip: enumDocumentType[] = [enumDocumentType.BILL_OF_LADING, enumDocumentType.FILE_PREVIEW];
    // #5820
    if (!this.isESSynced && !this.isHasEsOcrRedesign) {
      typesToSkip.push(enumDocumentType.INVOICE);
    }

    if (this.entrySummary.dutiesPaymentType === enumEntrySummaryDutiesPaymentType.INSTANT) {
      typesToSkip.push(enumDocumentType.ACH_APPROVAL);
    }

    this.attachedDocuments = [...documents]
      .filter((doc: DocumentModel) => !typesToSkip.includes(doc.type))
      .sort((a: DocumentModel, b: DocumentModel) => a.id - b.id);

    // add docs of this type to the beginning (create empty if none)
    // sort them via fake-id or created id
    // & create result array
    const docsToAdd: enumDocumentType[] = [enumDocumentType.PACKING_LIST, enumDocumentType.ARRIVAL_NOTICE];
    docsToAdd.push(enumDocumentType.POWER_OF_ATTORNEY);

    if (this.entrySummary.dutiesPaymentType === enumEntrySummaryDutiesPaymentType.ACH) {
      docsToAdd.push(enumDocumentType.ACH_APPROVAL);
    }

    docsToAdd.forEach((documentType: enumDocumentType, index: number) => {
      if (!documents.find((document: DocumentModel) => document.type === documentType)) {
        this.attachedDocuments.push({
          relatedEntityId: this.entrySummary.id,
          type: documentType,
          relatedEntityType: enumDocumentRelatedEntityType.ENTRY_SUMMARY,
          rootEntityType: enumDocumentRootEntityType.ENTRY_SUMMARY,
          rootEntityId: this.entrySummary.id,
          url: null,
          createdDate: null,
          id: index,
          fileName: null,
          state: enumDocumentState.CREATED,
        });
      }
    });

    this.attachedDocuments = [
      ...this.attachedDocuments.filter(
        (doc: DocumentModel) => doc.type === enumDocumentType.POWER_OF_ATTORNEY || doc.type === enumDocumentType.POWER_OF_ATTORNEY_TRUCKER,
      ),
      ...this.attachedDocuments.filter((doc: DocumentModel) => doc.type === enumDocumentType.ACH_APPROVAL),
      ...this.attachedDocuments.filter((doc: DocumentModel) => doc.type === enumDocumentType.ARRIVAL_NOTICE),
      ...this.attachedDocuments.filter((doc: DocumentModel) => doc.type === enumDocumentType.PACKING_LIST),
      ...this.attachedDocuments.filter((doc: DocumentModel) => !docsToAdd.includes(doc.type)),
    ];
  }
}
